import { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../auth/msalConfig';
import {
  fetchRounds,
  fetchRound,
  createRound,
  updateRoundMessages,
  deleteRound,
  type HireRatingRoundSummary,
  type HireRatingRoundDetail,
} from '../../api/hireRatingRoundApi';
import {
  fetchHireEvents,
  createHireEvent,
  deleteHireEvent,
  deliverRound,
  uploadHireEvents,
  getHireEventsTemplateUrl,
  type HireRatingEventRecord,
  type HireRatingImportResult,
} from '../../api/hireRatingEventsApi';

// ─── Default messages ─────────────────────────────────────────────────
const DEFAULT_INVITATION_MESSAGE =
  "Hi [First Name], it's time for our hire satisfaction review. " +
  'Please take a few minutes to rate the employees who joined your team in [Cohort Period]. ' +
  'Your feedback helps us understand the quality of our hiring and improve our recruitment approach. ' +
  'Click below to get started.';

const DEFAULT_LANDING_PAGE_MESSAGE =
  'Welcome to the [Cohort Period] hire satisfaction review. ' +
  'You have [Hire Count] hire(s) to rate in this session. ' +
  "For each person, you'll be asked to rate their quality of hire, performance against expectations, " +
  'cultural fit, and whether you would hire them again. ' +
  'You can save your progress and return at any time before [Close Date].';

// ─── Shared input style ───────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  fontSize: '0.9rem',
  border: '1px solid var(--color-border)',
  borderRadius: 4,
  background: 'var(--color-surface)',
  width: '100%',
};

// ─── Add hire form ────────────────────────────────────────────────────
function AddHireForm({
  roundId,
  getToken,
  onAdded,
}: {
  roundId: string;
  getToken: () => Promise<string>;
  onAdded: () => void;
}) {
  const empty = { hireeName: '', hireeRole: '', department: '', managerEmail: '', managerName: '', hireDate: '', requisitionSource: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [k]: e.target.value }));

  const handleSubmit = async () => {
    const { hireeName, hireeRole, department, managerEmail, managerName, hireDate } = form;
    if (!hireeName || !hireeRole || !department || !managerEmail || !managerName || !hireDate) {
      setError('All fields except Source are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      await createHireEvent(token, {
        hireeName, hireeRole, department,
        managerEmail, managerName,
        hireDate: new Date(hireDate).toISOString(),
        requisitionSource: form.requisitionSource || undefined,
        roundId,
      });
      setForm(empty);
      onAdded();
    } catch {
      setError('Failed to add hire.');
      setSaving(false);
    }
  };

  return (
    <div style={{ border: '1px dashed var(--color-border)', borderRadius: 8, padding: '1rem', marginTop: '0.75rem' }}>
      <p style={{ margin: '0 0 0.75rem', fontWeight: 600, fontSize: '0.875rem' }}>Add hire to round</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
        <div><label style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>Full name *</label><input style={inputStyle} value={form.hireeName} onChange={set('hireeName')} placeholder="Jane Smith" /></div>
        <div><label style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>Role *</label><input style={inputStyle} value={form.hireeRole} onChange={set('hireeRole')} placeholder="Software Engineer" /></div>
        <div><label style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>Department *</label><input style={inputStyle} value={form.department} onChange={set('department')} placeholder="Engineering" /></div>
        <div><label style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>Start date *</label><input style={inputStyle} type="date" value={form.hireDate} onChange={set('hireDate')} /></div>
        <div><label style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>Manager email *</label><input style={inputStyle} value={form.managerEmail} onChange={set('managerEmail')} placeholder="manager@gridworks.com" /></div>
        <div><label style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>Manager name *</label><input style={inputStyle} value={form.managerName} onChange={set('managerName')} placeholder="John Manager" /></div>
        <div><label style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>Source (optional)</label><input style={inputStyle} value={form.requisitionSource} onChange={set('requisitionSource')} placeholder="Job Board" /></div>
      </div>
      {error && <p role="alert" style={{ color: 'var(--color-error)', fontSize: '0.8rem', marginTop: '0.5rem' }}>{error}</p>}
      <button className="btn btn-primary" style={{ minHeight: 32, marginTop: '0.75rem', padding: '0 1rem' }} onClick={handleSubmit} disabled={saving}>
        {saving ? 'Adding…' : 'Add hire'}
      </button>
    </div>
  );
}

// ─── Hires table ──────────────────────────────────────────────────────
function HiresPanel({
  roundId,
  getToken,
}: {
  roundId: string;
  getToken: () => Promise<string>;
}) {
  const [events, setEvents] = useState<HireRatingEventRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [deliveryResult, setDeliveryResult] = useState<string | null>(null);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<HireRatingImportResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      const token = await getToken();
      const data = await fetchHireEvents(token, { roundId });
      setEvents(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [roundId]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    setUploadError(null);
    try {
      const token = await getToken();
      const result = await uploadHireEvents(token, file, roundId);
      setUploadResult(result);
      if (result.created > 0) load();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
      if (uploadRef.current) uploadRef.current.value = '';
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Remove this hire from the round?')) return;
    try {
      const token = await getToken();
      await deleteHireEvent(token, id);
      setEvents(prev => prev.filter(e => e.id !== id));
    } catch {
      alert('Could not delete — this hire may already have ratings.');
    }
  };

  const handleDeliver = async () => {
    setDelivering(true);
    setDeliveryResult(null);
    setDeliveryError(null);
    try {
      const token = await getToken();
      const result = await deliverRound(token, roundId);
      if (result.succeeded > 0 || result.skipped > 0) {
        setDeliveryResult(
          `${result.succeeded} invitation${result.succeeded !== 1 ? 's' : ''} sent` +
          (result.skipped > 0 ? `, ${result.skipped} already delivered` : '') +
          (result.failed > 0 ? `, ${result.failed} failed` : '') +
          '.'
        );
      } else {
        setDeliveryResult('No invitations sent — check that hires have a manager email set.');
      }
    } catch {
      setDeliveryError('Delivery failed. Check the API logs.');
    } finally {
      setDelivering(false);
    }
  };

  if (loading) return <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginTop: '0.75rem' }}>Loading hires…</p>;

  const unrated = events.filter(e => e.ratedCount === 0);

  return (
    <div style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
          Hires ({events.length})
        </span>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            className="btn btn-ghost"
            style={{ minHeight: 28, padding: '0 0.75rem', fontSize: '0.8rem' }}
            onClick={() => setShowAdd(v => !v)}
          >
            {showAdd ? 'Cancel' : '+ Add hire'}
          </button>
          <label
            className="btn btn-ghost"
            style={{ minHeight: 28, padding: '0 0.75rem', fontSize: '0.8rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
            title="Bulk-add hires from an Excel file"
          >
            {uploading ? 'Uploading…' : '↑ Upload Excel'}
            <input
              ref={uploadRef}
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              disabled={uploading}
              onChange={handleUpload}
            />
          </label>
          <a
            href={getHireEventsTemplateUrl()}
            download
            className="btn btn-ghost"
            style={{ minHeight: 28, padding: '0 0.75rem', fontSize: '0.8rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
          >
            ↓ Template
          </a>
          {unrated.length > 0 && (
            <button
              className="btn btn-primary"
              style={{ minHeight: 28, padding: '0 0.75rem', fontSize: '0.8rem' }}
              onClick={handleDeliver}
              disabled={delivering}
            >
              {delivering ? 'Sending…' : `Send invitations (${unrated.length})`}
            </button>
          )}
        </div>
      </div>

      {deliveryResult && (
        <p style={{ color: 'var(--color-success)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>{deliveryResult}</p>
      )}
      {deliveryError && (
        <p role="alert" style={{ color: 'var(--color-error)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>{deliveryError}</p>
      )}
      {uploadResult && (
        <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem', color: uploadResult.created > 0 ? 'var(--color-success)' : 'var(--color-text-muted)' }}>
          {uploadResult.created > 0
            ? `${uploadResult.created} hire${uploadResult.created !== 1 ? 's' : ''} added from Excel.`
            : 'No hires added.'}
          {uploadResult.skipped > 0 && ` ${uploadResult.skipped} row${uploadResult.skipped !== 1 ? 's' : ''} skipped.`}
          {uploadResult.errors.length > 0 && (
            <details style={{ marginTop: '0.25rem' }}>
              <summary style={{ cursor: 'pointer', color: 'var(--color-text-muted)' }}>
                {uploadResult.errors.length} issue{uploadResult.errors.length !== 1 ? 's' : ''}
              </summary>
              <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.25rem', fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                {uploadResult.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
      {uploadError && (
        <p role="alert" style={{ color: 'var(--color-error)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>{uploadError}</p>
      )}

      {events.length === 0 ? (
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>No hires in this round yet.</p>
      ) : (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden' }}>
          {events.map((ev, i) => (
            <div key={ev.id} style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr auto',
              gap: '0.5rem',
              padding: '0.6rem 0.75rem',
              borderTop: i > 0 ? '1px solid var(--color-border)' : undefined,
              background: ev.ratedCount > 0 ? 'var(--color-info-bg)' : undefined,
              alignItems: 'center',
            }}>
              <div>
                <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{ev.hireeName}</span>
                <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', marginLeft: '0.5rem' }}>{ev.hireeRole}</span>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                {ev.managerName} · {new Date(ev.hireDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                {ev.ratedCount > 0 && <span style={{ color: 'var(--color-success)', marginLeft: '0.5rem' }}>✓ Rated</span>}
              </div>
              <button
                onClick={() => handleDelete(ev.id)}
                disabled={ev.ratedCount > 0}
                title={ev.ratedCount > 0 ? 'Already rated — cannot remove' : 'Remove hire'}
                style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: ev.ratedCount > 0 ? 'not-allowed' : 'pointer', fontSize: '1rem', padding: '0 0.25rem', opacity: ev.ratedCount > 0 ? 0.4 : 1 }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddHireForm roundId={roundId} getToken={getToken} onAdded={() => { setShowAdd(false); load(); }} />
      )}
    </div>
  );
}

// ─── Message editor ───────────────────────────────────────────────────
function MessageEditor({
  round,
  getToken,
  onSaved,
  onClose,
}: {
  round: HireRatingRoundDetail;
  getToken: () => Promise<string>;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [invitationMessage, setInvitationMessage] = useState(round.invitationMessage);
  const [landingPageMessage, setLandingPageMessage] = useState(round.landingPageMessage);
  const [closeDate, setCloseDate] = useState(round.closeDate ? round.closeDate.slice(0, 16) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      await updateRoundMessages(token, round.id, invitationMessage, landingPageMessage,
        closeDate ? new Date(closeDate).toISOString() : undefined);
      onSaved();
    } catch {
      setError('Failed to save.');
      setSaving(false);
    }
  };

  const taStyle: React.CSSProperties = { fontSize: '0.875rem', width: '100%', minHeight: '5rem', padding: '0.6rem', fontFamily: 'var(--font-base)', border: '1px solid var(--color-border)', borderRadius: 4, resize: 'vertical' };

  return (
    <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1rem', marginTop: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <strong style={{ fontSize: '0.875rem' }}>Messages</strong>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }} onClick={onClose}>✕</button>
      </div>

      <div style={{ marginBottom: '0.75rem' }}>
        <label style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Close date</label>
        <input type="datetime-local" value={closeDate} onChange={e => setCloseDate(e.target.value)} style={{ ...inputStyle, width: 'auto' }} />
      </div>

      <div style={{ marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
          <label style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>Invitation · [First Name] [Cohort Period] [Close Date] [Hire Count]</label>
          <button type="button" style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontSize: '0.75rem', cursor: 'pointer', padding: 0 }} onClick={() => setInvitationMessage(DEFAULT_INVITATION_MESSAGE)}>Reset</button>
        </div>
        <textarea style={taStyle} value={invitationMessage} rows={4} onChange={e => setInvitationMessage(e.target.value)} />
      </div>

      <div style={{ marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
          <label style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>Landing page · [First Name] [Cohort Period] [Close Date] [Hire Count]</label>
          <button type="button" style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontSize: '0.75rem', cursor: 'pointer', padding: 0 }} onClick={() => setLandingPageMessage(DEFAULT_LANDING_PAGE_MESSAGE)}>Reset</button>
        </div>
        <textarea style={taStyle} value={landingPageMessage} rows={4} onChange={e => setLandingPageMessage(e.target.value)} />
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-error)', fontSize: '0.875rem' }}>{error}</p>}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button className="btn btn-primary" style={{ minHeight: 32 }} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        <button className="btn btn-ghost" style={{ minHeight: 32 }} onClick={onClose} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}

// ─── Round row ────────────────────────────────────────────────────────
function RoundRow({
  round,
  getToken,
  onChanged,
}: {
  round: HireRatingRoundSummary;
  getToken: () => Promise<string>;
  onChanged: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editingMessages, setEditingMessages] = useState(false);
  const [detail, setDetail] = useState<HireRatingRoundDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(
      `Delete "${round.cohortPeriod}"? Any hires in this round will be unassigned but not deleted.`
    )) return;
    setDeleting(true);
    try {
      const token = await getToken();
      await deleteRound(token, round.id);
      onChanged();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      alert(msg.includes('409') || msg.toLowerCase().includes('ratings')
        ? 'Cannot delete — this round already has hire ratings.'
        : 'Delete failed.');
      setDeleting(false);
    }
  };

  const expand = async () => {
    if (!expanded && !detail) {
      setLoadingDetail(true);
      try {
        const token = await getToken();
        const { fetchRound } = await import('../../api/hireRatingRoundApi');
        setDetail(await fetchRound(round.id, token));
      } catch {
        // ignore
      } finally {
        setLoadingDetail(false);
      }
    }
    setExpanded(v => !v);
  };

  return (
    <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.875rem 1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', flex: 1, minWidth: 0 }}>
          <button
            onClick={expand}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '1rem', padding: 0, textAlign: 'left', color: 'var(--color-text)' }}
          >
            {expanded ? '▾' : '▸'} {round.cohortPeriod}
          </button>
          <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
            {round.eventCount} hire{round.eventCount !== 1 ? 's' : ''}
          </span>
          {round.closeDate && (
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
              closes {new Date(round.closeDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          <button
            className="btn btn-ghost"
            style={{ minHeight: 28, padding: '0 0.75rem', fontSize: '0.8rem' }}
            onClick={async () => {
              if (!detail) {
                setLoadingDetail(true);
                try {
                  const token = await getToken();
                  const { fetchRound } = await import('../../api/hireRatingRoundApi');
                  setDetail(await fetchRound(round.id, token));
                } finally {
                  setLoadingDetail(false);
                }
              }
              setExpanded(true);
              setEditingMessages(v => !v);
            }}
            disabled={loadingDetail}
          >
            {editingMessages ? 'Hide messages' : 'Edit messages'}
          </button>
          <button
            className="btn btn-ghost"
            style={{ minHeight: 28, padding: '0 0.75rem', fontSize: '0.8rem', color: 'var(--color-error)' }}
            onClick={handleDelete}
            disabled={deleting}
            title="Delete this cohort period"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ borderTop: '1px solid var(--color-border)', padding: '0.75rem 1.25rem 1.25rem' }}>
          {loadingDetail ? (
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>Loading…</p>
          ) : (
            <>
              <HiresPanel roundId={round.id} getToken={getToken} />

              {editingMessages && detail && (
                <MessageEditor
                  round={detail}
                  getToken={getToken}
                  onSaved={() => { setEditingMessages(false); onChanged(); }}
                  onClose={() => setEditingMessages(false)}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────
export function HireRatingRounds() {
  const { instance, accounts } = useMsal();
  const [rounds, setRounds] = useState<HireRatingRoundSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [cohortPeriod, setCohortPeriod] = useState('');
  const [closeDate, setCloseDate] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const getToken = async () => {
    const r = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return r.accessToken;
  };

  const loadRounds = async () => {
    try {
      const token = await getToken();
      setRounds(await fetchRounds(token));
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRounds(); }, []);

  const handleCreate = async () => {
    if (!cohortPeriod.trim()) { setCreateError('Cohort period is required.'); return; }
    setCreating(true);
    setCreateError(null);
    try {
      const token = await getToken();
      await createRound(token, cohortPeriod.trim(), closeDate ? new Date(closeDate).toISOString() : undefined);
      setCohortPeriod('');
      setCloseDate('');
      await loadRounds();
    } catch {
      setCreateError('Failed to create round.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>;

  return (
    <div style={{ maxWidth: 800 }}>
      <h1 style={{ margin: '0 0 1.5rem', fontSize: '1.5rem' }}>Hire Rating Rounds</h1>

      {/* Create round */}
      <section style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, padding: '1.25rem', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>New round</h2>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>Cohort period *</label>
            <input type="text" placeholder="e.g. Q1 2026" value={cohortPeriod} onChange={e => setCohortPeriod(e.target.value)}
              style={{ padding: '0.4rem 0.6rem', fontSize: '0.9rem', border: '1px solid var(--color-border)', borderRadius: 4, width: 160 }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>Close date (optional)</label>
            <input type="datetime-local" value={closeDate} onChange={e => setCloseDate(e.target.value)}
              style={{ padding: '0.4rem 0.6rem', fontSize: '0.9rem', border: '1px solid var(--color-border)', borderRadius: 4, background: 'var(--color-surface)' }} />
          </div>
          <button className="btn btn-primary" style={{ minHeight: 36 }} onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating…' : 'Create round'}
          </button>
        </div>
        {createError && <p role="alert" style={{ color: 'var(--color-error)', fontSize: '0.875rem', marginTop: '0.5rem' }}>{createError}</p>}
      </section>

      {/* Round list */}
      {rounds.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>No rounds yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {rounds.map(r => (
            <RoundRow key={r.id} round={r} getToken={getToken} onChanged={loadRounds} />
          ))}
        </div>
      )}
    </div>
  );
}
